/**
 * Opening-local → world mapping of door/window glyphs, bending along curved
 * walls.
 *
 * Glyphs are drawn in the opening's local frame: `u` along the wall from the
 * opening center, `n` across it from the symbol's line (see OpeningFrame). On
 * a straight wall that is a plain rigid frame. On a curved wall (a bulged
 * edge) `u` is the ARC LENGTH along the wall centerline and `n` the RADIAL
 * distance off the symbol's line — so glass lines follow the curve, the jambs
 * stay radial (like the cut hole, see tessellateBand) and a door leaf stands
 * perpendicular to the wall at its hinge.
 */

import type { OpeningFrame, Pt2 } from '../FloorMesh3DBuilder';

/** Below this |curvature| (1/m, radius > 1 km) a wall counts as straight. */
const STRAIGHT_CURVATURE = 1e-6;

/** Longest straight piece a local segment keeps on a curved wall (m) — the sag of a 0.1 m chord on a 1 m radius is ~1.3 mm. */
const MAX_BENT_PIECE = 0.1;

/** Upper bound of pieces per local segment (very long pocket-door dashes on tight curves). */
const MAX_PIECES = 64;

/** Whether glyphs in this frame bend (the opening sits on a curved wall). */
export function isBentOpeningFrame(frame: OpeningFrame): boolean {
  return !!frame.bend && Math.abs(frame.bend.curvature) > STRAIGHT_CURVATURE;
}

/** The same frame at the origin, unbent (local drawing space: x = u, y = n). */
export function localOpeningFrame(halfWidth: number): OpeningFrame {
  return { center: { x: 0, y: 0 }, wallDir: { x: 1, y: 0 }, wallNorm: { x: 0, y: 1 }, halfWidth };
}

/** World point of the local (u, n): rigid on straight walls, arc length / radial offset on curved ones. */
export function openingPointAt(frame: OpeningFrame, u: number, n: number): Pt2 {
  const { center, wallDir, wallNorm } = frame;
  if (!isBentOpeningFrame(frame) || !frame.bend) {
    return { x: center.x + u * wallDir.x + n * wallNorm.x, y: center.y + u * wallDir.y + n * wallNorm.y };
  }
  const { curvature, perpOffset } = frame.bend;
  // Centerline point under the opening center, and the circle the centerline runs on.
  const p0 = { x: center.x - wallNorm.x * perpOffset, y: center.y - wallNorm.y * perpOffset };
  const c = { x: p0.x + wallNorm.x / curvature, y: p0.y + wallNorm.y / curvature };
  // Walking u along the centerline turns the frame by u·κ (CCW for κ > 0: the wall bends toward +wallNorm).
  const phi = u * curvature;
  const cos = Math.cos(phi);
  const sin = Math.sin(phi);
  const vx = p0.x - c.x;
  const vy = p0.y - c.y;
  const normX = wallNorm.x * cos - wallNorm.y * sin;
  const normY = wallNorm.x * sin + wallNorm.y * cos;
  const off = perpOffset + n;
  return { x: c.x + vx * cos - vy * sin + normX * off, y: c.y + vx * sin + vy * cos + normY * off };
}

/**
 * Local (u, n) of a world point — the inverse of {@link openingPointAt}
 * (for dragging on a bent frame). `u` is unwrapped to (−πR, πR].
 */
export function openingLocalOf(frame: OpeningFrame, p: Pt2): { u: number; n: number } {
  const { center, wallDir, wallNorm } = frame;
  if (!isBentOpeningFrame(frame) || !frame.bend) {
    const dx = p.x - center.x;
    const dy = p.y - center.y;
    return { u: dx * wallDir.x + dy * wallDir.y, n: dx * wallNorm.x + dy * wallNorm.y };
  }
  const { curvature, perpOffset } = frame.bend;
  const p0 = { x: center.x - wallNorm.x * perpOffset, y: center.y - wallNorm.y * perpOffset };
  const c = { x: p0.x + wallNorm.x / curvature, y: p0.y + wallNorm.y / curvature };
  const a0 = Math.atan2(p0.y - c.y, p0.x - c.x);
  const a = Math.atan2(p.y - c.y, p.x - c.x);
  let phi = a - a0;
  while (phi > Math.PI) phi -= 2 * Math.PI;
  while (phi <= -Math.PI) phi += 2 * Math.PI;
  const radius = 1 / Math.abs(curvature);
  const dist = Math.hypot(p.x - c.x, p.y - c.y);
  // Radial offset off the centerline, measured along the (turned) wallNorm: toward the center for κ > 0.
  const off = Math.sign(curvature) * (radius - dist);
  return { u: phi / curvature, n: off - perpOffset };
}

/**
 * Map local segments (`[u1,n1,_, u2,n2,_, …]`, every 6 numbers one segment)
 * into `frame` at height `z`. On a curved wall each segment is split into
 * short pieces first, so straight local lines follow the curve.
 */
export function placeOpeningSegments(local: readonly number[], frame: OpeningFrame, z: number, out: number[] = []): number[] {
  const bent = isBentOpeningFrame(frame);
  for (let i = 0; i + 5 < local.length; i += 6) {
    const u0 = local[i];
    const n0 = local[i + 1];
    const u1 = local[i + 3];
    const n1 = local[i + 4];
    const pieces = bent ? Math.min(MAX_PIECES, Math.max(1, Math.ceil(Math.hypot(u1 - u0, n1 - n0) / MAX_BENT_PIECE))) : 1;
    let prev = openingPointAt(frame, u0, n0);
    for (let k = 1; k <= pieces; k++) {
      const t = k / pieces;
      const next = openingPointAt(frame, u0 + (u1 - u0) * t, n0 + (n1 - n0) * t);
      out.push(prev.x, prev.y, z, next.x, next.y, z);
      prev = next;
    }
  }
  return out;
}
